import {
  SURVEY_TEXT_MAX_LENGTH,
  type SurveyAnswerInput,
  type SurveyPublicQuestion,
} from '../../api/survey'

export type AnswersByQuestion = Record<number, SurveyAnswerInput>

export const truncateTextAnswer = (value: string): string =>
  Array.from(value).slice(0, SURVEY_TEXT_MAX_LENGTH).join('')

const getPrefilledAnswer = (
  question: SurveyPublicQuestion,
): SurveyAnswerInput | null => {
  if (!question.answered || !question.answer) return null

  switch (question.type) {
    case 'SINGLE':
    case 'MULTI':
      return {
        questionId: question.id,
        choiceIds: question.answer.choiceIds ?? [],
      }
    case 'TEXT':
      return {
        questionId: question.id,
        textValue: question.answer.textValue ?? '',
      }
    case 'DATE':
      return {
        questionId: question.id,
        dateValue: question.answer.dateValue ?? '',
      }
  }
}

export const getPrefilledAnswers = (
  questions: SurveyPublicQuestion[],
): AnswersByQuestion =>
  questions.reduce<AnswersByQuestion>((result, question) => {
    const answer = getPrefilledAnswer(question)
    if (answer) result[question.id] = answer
    return result
  }, {})

export const hasAnswer = (
  question: SurveyPublicQuestion,
  answer?: SurveyAnswerInput,
): boolean => {
  if (!answer) return false

  switch (question.type) {
    case 'SINGLE':
    case 'MULTI':
      return Boolean(answer.choiceIds?.length)
    case 'TEXT':
      return Boolean(answer.textValue?.trim())
    case 'DATE':
      return Boolean(answer.dateValue)
  }
}

const getNewAnswer = (
  question: SurveyPublicQuestion,
  answer?: SurveyAnswerInput,
): SurveyAnswerInput | null => {
  if (question.answered || !answer) return null

  switch (question.type) {
    case 'SINGLE':
    case 'MULTI': {
      const choiceIds = answer.choiceIds ?? []
      return choiceIds.length > 0
        ? { questionId: question.id, choiceIds }
        : null
    }
    case 'TEXT': {
      const textValue = answer.textValue?.trim()
      return textValue
        ? {
            questionId: question.id,
            textValue: truncateTextAnswer(textValue),
          }
        : null
    }
    case 'DATE':
      return answer.dateValue
        ? { questionId: question.id, dateValue: answer.dateValue }
        : null
  }
}

export const getNewAnswers = (
  questions: SurveyPublicQuestion[],
  answers: AnswersByQuestion,
): SurveyAnswerInput[] =>
  questions.reduce<SurveyAnswerInput[]>((result, question) => {
    const answer = getNewAnswer(question, answers[question.id])
    if (answer) result.push(answer)
    return result
  }, [])
